/* eslint-disable no-nested-ternary */
import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as vp from 'vopay';

const { Table, Select, Box, ProgressChart, Tooltip } = vp;

type AccountsOverviewTableProps = {
  period: string;
  periodOptions: {
    [key: string]: string;
  };
  transactionOptions: {
    [key: string]: string;
  };
  statusOrder: string[];
  accountsData: {
    Statistics: {
      [StatType: string]: {
        numOfData: {
          yearly: number;
          monthly: number;
          weekly: number;
        };
        volOfData: {
          yearly: number;
          monthly: number;
          weekly: number;
        };
      };
    };
  };
};

type StatusKeyValuePair = {
  display: string | React.ReactNode;
  value: number;
  backgroundColor: string;
  percentComplete: number;
};

type FC = FunctionComponent<AccountsOverviewTableProps>;

const AccountsOverviewTable: FC = (props: AccountsOverviewTableProps) => {
  const { period, periodOptions, transactionOptions, statusOrder, accountsData } = props;
  const [transaction, setTransaction] = useState('Num');
  const { t } = useTranslation('translations', { keyPrefix: 'HomePage.AccountOverviewWidget.ActiveTab' });

  const numOfDataYearly = statusOrder.map((status) => Number(accountsData.Statistics[status]?.numOfData.yearly ?? 0));
  const numOfDataMonthly = statusOrder.map((status) => Number(accountsData.Statistics[status]?.numOfData.monthly ?? 0));
  const numOfDataWeekly = statusOrder.map((status) => Number(accountsData.Statistics[status]?.numOfData.weekly ?? 0));
  const volOfDataYearly = statusOrder.map((status) => Number(accountsData.Statistics[status]?.volOfData.yearly ?? 0));
  const volOfDataMonthly = statusOrder.map((status) => Number(accountsData.Statistics[status]?.volOfData.monthly ?? 0));
  const volOfDataWeekly = statusOrder.map((status) => Number(accountsData.Statistics[status]?.volOfData.weekly ?? 0));

  const handleTransactionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTransaction(event.currentTarget.value);
  };

  const handlePercentComplete = (position: number) => {
    const percentComplete =
      period === Object.keys(periodOptions)[0] && transaction === Object.keys(transactionOptions)[0]
        ? (numOfDataMonthly[position] / numOfDataMonthly.reduce((res, num) => res + num, 0)) * 100
        : period === Object.keys(periodOptions)[1] && transaction === Object.keys(transactionOptions)[0]
          ? (numOfDataYearly[position] / numOfDataYearly.reduce((res, num) => res + num, 0)) * 100
          : period === Object.keys(periodOptions)[2] && transaction === Object.keys(transactionOptions)[0]
            ? (numOfDataWeekly[position] / numOfDataWeekly.reduce((res, num) => res + num, 0)) * 100
            : period === Object.keys(periodOptions)[0] && transaction === Object.keys(transactionOptions)[1]
              ? (volOfDataMonthly[position] / volOfDataMonthly.reduce((res, num) => res + num, 0)) * 100
              : period === Object.keys(periodOptions)[1] && transaction === Object.keys(transactionOptions)[1]
                ? (volOfDataYearly[position] / volOfDataYearly.reduce((res, num) => res + num, 0)) * 100
                : period === Object.keys(periodOptions)[2] && transaction === Object.keys(transactionOptions)[1]
                  ? (volOfDataWeekly[position] / volOfDataWeekly.reduce((res, num) => res + num, 0)) * 100
                  : 0;
    return percentComplete;
  };

  const AccountsOverviewRows: StatusKeyValuePair[] = statusOrder.map((status, index) => {
    const bgColors: Record<string, string> = {
      complete: '#28BE9A',
      inProgress: '#C4C4C4',
      pending: '#EBEDEF',
    };

    const value =
      transaction === Object.keys(transactionOptions)[0]
        ? period === Object.keys(periodOptions)[0]
          ? numOfDataMonthly[index]
          : period === Object.keys(periodOptions)[1]
            ? numOfDataYearly[index]
            : numOfDataWeekly[index]
        : period === Object.keys(periodOptions)[0]
          ? volOfDataMonthly[index]
          : period === Object.keys(periodOptions)[1]
            ? volOfDataYearly[index]
            : volOfDataWeekly[index];

    const displayText =
      status === 'complete'
        ? t('Rows.complete', { defaultValue: 'Complete' })
        : status === 'inProgress'
          ? t('Rows.inProgress', { defaultValue: 'In Progress' })
          : t('Rows.pending', { defaultValue: 'Pending' });

    const displayWithTooltip =
      status === 'complete' ? (
        <Box jc="fs" ai="center" gapSize="4px">
          {displayText}{' '}
          <Tooltip
            position="right"
            information={t('Rows.completeTooltip', {
              defaultValue: 'Includes Successful, Cancelled, Failed, Declined, Settled and Complete transactions.',
            })}
          />
        </Box>
      ) : (
        displayText
      );

    return {
      display: displayWithTooltip,
      value,
      backgroundColor: bgColors[status],
      percentComplete: handlePercentComplete(index),
    };
  });

  const tableProps: vp.TableProps = {
    name: 'accounts-overview-table',
    variant: 'chart',
    columns: {
      statusKey: { header: t('status', { defaultValue: 'Status' }) },
      value: {
        header: (
          <Select
            variant="chart"
            options={transactionOptions}
            placeholder={transactionOptions.total}
            name="accounts-overview-transaction-select"
            value={transaction}
            onBlur={() => {}}
            onChange={handleTransactionChange}
          />
        ),
      },
    },
    rows: AccountsOverviewRows.map(({ display, value, backgroundColor, percentComplete }) => ({
      statusKey: display,
      value: (
        <Box width="100%" jc="fe" ai="center">
          <Box jc="fe">
            <ProgressChart variant="chart" backgroundColor={backgroundColor} percentComplete={percentComplete} />
          </Box>
          <Box jc="fe">
            <div style={{ minWidth: 122, paddingLeft: 10 }}>
              {transaction === Object.keys(transactionOptions)[1]
                ? `${value === undefined ? 0 : value?.toLocaleString()}`
                : `$${value === undefined ? 0 : value?.toLocaleString()}`}
            </div>
          </Box>
        </Box>
      ),
    })),
  };

  return <Table {...tableProps} />;
};

export default AccountsOverviewTable;
